/**
 * Shared client-side helpers for `stocktake_line.count_expression`.
 *
 * Mirror of `app/articles/count_expression.py`. The desktop chip strip,
 * the mobile keypad, the ledger row renderer, and the resume buffer
 * all consume these helpers.
 *
 * The TERM SHAPE matches the backend exactly:
 *   - {kind: "loose", n}                       → +n
 *   - {kind: "broken", n}                      → −n
 *   - {kind: "crates", n, pack_size}           → +n × pack_size
 *
 * `pack_size` is SNAPSHOTTED on each `crates` term at commit time, NOT
 * looked up live — matches the buy_price_cents_at_finalize / pfand
 * snapshot pattern. The `sealed` boolean on a synthesized legacy term
 * is a synthetic read-only marker; it is NEVER round-tripped to the
 * server.
 *
 * The invariant `sum(signed_terms) == counted_qty` is enforced
 * server-side by the mutation helper. The client SHOULD compute it
 * before submitting so the operator gets an immediate inline error,
 * but the server is the source of truth.
 *
 * OpenSpec: openspec/changes/add-stocktake-count-expression/specs/
 * stock-management/spec.md.
 */

export type TermKind = "loose" | "crates" | "broken";

export interface LooseTerm {
  kind: "loose";
  n: number;
  /** Read-only synthesized flag on legacy lines. NEVER persist. */
  sealed?: boolean;
}

export interface CratesTerm {
  kind: "crates";
  n: number;
  /** Snapshotted from `article_metadata.pack_size` at commit time. */
  pack_size: number;
  sealed?: boolean;
}

export interface BrokenTerm {
  kind: "broken";
  n: number;
  sealed?: boolean;
}

export type Term = LooseTerm | CratesTerm | BrokenTerm;

/** Result of parsing one typed-input commit on desktop. */
export type ParseResult =
  | { ok: true; term: Term }
  | { ok: false; error: string };

/** Signed contribution of a single term. Throws on a malformed crates
 * term missing pack_size (defensive — parse paths should reject these
 * before this function ever sees them). */
export function signed(term: Term): number {
  if (term.kind === "loose") return term.n;
  if (term.kind === "broken") return -term.n;
  if (!term.pack_size || term.pack_size <= 0) {
    throw new Error("crates term missing positive pack_size");
  }
  return term.n * term.pack_size;
}

/** Sum of signed contributions across a term list. */
export function sumSigned(terms: readonly Term[]): number {
  let total = 0;
  for (const t of terms) total += signed(t);
  return total;
}

/** True iff any term carries the sealed read-only flag — i.e. the
 * line is a pre-V1 legacy row on first read. The chip strip uses
 * this to hide the × delete affordance until the operator adds a new
 * term. */
export function isSealed(terms: readonly Term[]): boolean {
  return terms.some((t) => t.sealed === true);
}

/** Strip the sealed flag before sending to the server. The backend
 * helper also strips it on its side (dump_json), but stripping
 * client-side keeps the payload honest. */
export function stripSealed(terms: readonly Term[]): Term[] {
  return terms.map((t) => {
    if (!t.sealed) return t;
    const copy = { ...t };
    delete copy.sealed;
    return copy;
  });
}

/** Render a term list as the operator-friendly math string used in
 * the desktop running-total and the mobile rolling expression. Examples:
 *   - `[{loose:5}]`                       → `"+5"`
 *   - `[{crates:3, pack_size:20}, {loose:2}]` → `"3×20 + 2"`
 *   - `[{loose:15}, {broken:4}]`          → `"15 − 4"`
 *
 * The leading sign is omitted on the very first term (matches the
 * spec mockups). Subsequent terms always carry an explicit `+` or `−`.
 */
export function render(terms: readonly Term[]): string {
  if (terms.length === 0) return "";
  const parts: string[] = [];
  terms.forEach((t, i) => {
    const lead = i === 0 ? "" : t.kind === "broken" ? " − " : " + ";
    if (t.kind === "loose") parts.push(`${lead}${t.n}`);
    else if (t.kind === "broken") parts.push(`${lead}${t.n}`);
    else parts.push(`${lead}${t.n}×${t.pack_size}`);
  });
  return parts.join("").trim();
}

/** Lazy synthesis on read — mirrors the backend's
 * `synthesize_legacy(counted_qty)`. Used by the SPA when a server
 * response carries `count_expression: null` but `counted_qty > 0`.
 *
 * Returns `null` for the uncounted case (counted_qty <= 0). */
export function synthesizeLegacy(countedQty: number): Term[] | null {
  if (countedQty <= 0) return null;
  return [{ kind: "loose", n: countedQty, sealed: true }];
}

/** Parse a typed-input commit from the desktop chip-strip input.
 *
 * Grammar (matches the spec's "Desktop Count Entry" requirement):
 *   - `N` or `+N`                           → loose
 *   - `xN`, `XN`, `×N`, `+xN`, etc.         → crates (requires pack_size)
 *   - `-N` (ASCII minus or Unicode minus)   → broken
 *
 * Whitespace is stripped. Decimals, multiple operators, leading minus
 * with no digits, and (when `packSize == null`) crates entries are
 * rejected.
 */
export function parseTypedInput(
  raw: string,
  packSize: number | null,
): ParseResult {
  // Strip whitespace + normalize unicode operator variants to ASCII.
  let s = raw.trim().replace(/\s+/g, "");
  s = s.replace(/×/g, "x").replace(/X/g, "x").replace(/−/g, "-");
  if (s === "") return { ok: false, error: "empty" };

  // Optional leading + (treated as no-op on positive kinds).
  if (s.startsWith("+")) s = s.slice(1);

  let kind: TermKind = "loose";
  if (s.startsWith("x")) {
    kind = "crates";
    s = s.slice(1);
  } else if (s.startsWith("-")) {
    kind = "broken";
    s = s.slice(1);
  }

  // What's left MUST be a non-negative integer with no further operators.
  if (!/^\d+$/.test(s)) {
    return { ok: false, error: "expected an integer (no decimals or extra signs)" };
  }
  const n = Number.parseInt(s, 10);
  if (!Number.isFinite(n) || n < 0) {
    return { ok: false, error: "n must be a non-negative integer" };
  }
  if (n === 0) {
    return { ok: false, error: "zero-quantity terms are not allowed" };
  }

  if (kind === "crates") {
    if (packSize == null || packSize <= 0) {
      return {
        ok: false,
        error: "no crate size set for this article",
      };
    }
    return { ok: true, term: { kind: "crates", n, pack_size: packSize } };
  }
  if (kind === "broken") {
    return { ok: true, term: { kind: "broken", n } };
  }
  return { ok: true, term: { kind: "loose", n } };
}

/** Check the running total invariant client-side before submitting.
 * Returns `null` if the invariant holds, or a short error string for
 * inline feedback. */
export function invariantViolation(
  terms: readonly Term[],
  countedQty: number,
): string | null {
  const sum = sumSigned(terms);
  if (sum !== countedQty) {
    return `terms sum to ${sum} but counted_qty is ${countedQty}`;
  }
  if (sum < 0) {
    return `running total is negative (${sum})`;
  }
  return null;
}
